"use client";

import CountUp from "react-countup";
import { useInView } from "react-intersection-observer";
import { Marquee } from "@/components/marquee";
import { useEffect, useState } from "react";

interface CountUpOnViewProps {
  stats: number;
  subtitle: string;
  image: React.ReactNode;
  idx: number;
}

export const Stats = () => {
  const images = [...Array(15)].map((_, idx) => `/stats/image_${idx + 1}.webp`);
  const [isSmall, setIsSmall] = useState<boolean>(false);
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  useEffect(() => {
    const handleResize = () => {
      setIsSmall(window.innerWidth <= 700);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div
      ref={ref}
      className="relative flex flex-col w-full h-fit items-center justify-center gap-12 lg:gap-24 px-6 py-12 lg:px-0 lg:py-28 overflow-hidden"
    >
      {/* Decoration */}
      <svg
        className={`absolute size-[60px] md:size-[125px] top-[30%] md:top-[50px] right-[4vw] md:left-[4vw] -z-20 transition-all duration-[1.5s] ease-out
            ${inView ? "scale-100 opacity-100" : "scale-[.25] opacity-25"}
          `}
        viewBox="0 0 125 125"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          opacity="0.35"
          d="M62.5 0L68.7983 29.3301C71.6894 42.7936 82.2064 53.3106 95.6699 56.2017L125 62.5L95.6699 68.7983C82.2064 71.6894 71.6894 82.2064 68.7983 95.6699L62.5 125L56.2017 95.6699C53.3106 82.2064 42.7936 71.6894 29.3301 68.7983L0 62.5L29.3301 56.2017C42.7936 53.3106 53.3106 42.7936 56.2017 29.3301L62.5 0Z"
          fill="#FFBD58"
        />
      </svg>

      {/* Hero content */}
      <div className="flex flex-col gap-4 items-center justify-center px-6 md:px-12 lg:px-0">
        <h2 className="text-3xl md:text-4xl lg:text-5xl text-natura-primary font-serifText text-center">
          Total Solution for Your Natural Smile
        </h2>
        <p className="text-base md:text-base lg:text-lg font-nunito font-normal text-center">
          Sudah 23 tahun lebih berpengalaman memberikan solusi lengkap untuk
          merawat keindahan senyum alami ribuan pasien.
        </p>
      </div>

      {/* Stats content */}
      <div className="flex md:flex-row flex-col gap-6 lg:gap-4 px-6 lg:px-0">
        {statsData.map((stats, idx) => (
          <CountUpOnView
            key={idx}
            stats={stats.stats}
            subtitle={stats.subtitle}
            image={stats.image}
            idx={idx}
          />
        ))}
      </div>

      {/* Marquee gallery */}
      <div className="w-full h-[200px] relative overflow-hidden grid place-items-center">
        <div className="absolute left-0 h-full w-14 md:w-60 bg-gradient-to-r from-white via-white/80 to-white/0 z-10" />
        <div className="absolute right-0 h-full w-14 md:w-60 bg-gradient-to-l from-white via-white/80 to-white/0 z-10" />

        {/* Marquee */}
        <Marquee
          images={images}
          speed={30}
          gap={16}
          width={isSmall ? 112 : 150}
          height={isSmall ? 150 : 200}
          imageMult={5}
        />
      </div>
    </div>
  );
};

const CountUpOnView = ({ stats, subtitle, image, idx }: CountUpOnViewProps) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  return (
    <div
      className="flex items-center justify-center gap-5 max-w-[240px] lg:max-w-full"
      ref={ref}
    >
      <div
        className={`flex size-16 p-4 lg:p-0 lg:w-24 lg:h-24 rounded-full bg-natura-primary relative items-center justify-center transition-all duration-500 ease-out
          ${inView ? "scale-100 opacity-100" : "scale-0 opacity-0"}
        `}
        style={{
          transitionDelay: `${0.15 * (idx + 1)}s`,
        }}
      >
        {image}
      </div>
      <div className="flex flex-col w-48 max-w-48 justify-center">
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-serifText leading-[1.2]">
          {inView ? (
            <CountUp
              start={0}
              end={stats}
              duration={2}
              suffix={`${idx === 1 ? "K+" : "+"}`}
            />
          ) : (
            0
          )}
        </h2>
        <p className="text-sm md:text-base lg:text-lg font-nunito">
          {subtitle}
        </p>
      </div>
    </div>
  );
};

const statsData = [
  {
    stats: 23,
    subtitle: "Tahun Melayani",
    image: (
      <svg
        className="stroke-[3px] stroke-white"
        width="44"
        height="54"
        viewBox="0 0 44 54"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M2 32.0011H7.98747C8.72246 32.0011 9.44746 32.1661 10.105 32.4861L15.2099 34.9561C15.8674 35.2736 16.5924 35.4386 17.3299 35.4386H19.9349C22.4549 35.4386 24.4999 37.4161 24.4999 39.856C24.4999 39.956 24.4324 40.041 24.3349 40.0685L17.9824 41.826C16.8426 42.1409 15.6271 42.0307 14.5624 41.516L9.10496 38.876M12.9899 3.38126C16.6749 1.09628 19.8949 2.01627 21.8274 3.48376C22.6199 4.08376 23.0174 4.38376 23.2499 4.38376C23.4824 4.38376 23.8799 4.08376 24.6724 3.48376C26.6049 2.01627 29.8223 1.09628 33.5098 3.38126C38.3498 6.38125 39.4448 16.2712 28.2849 24.6161C26.1574 26.2061 25.0949 27.0011 23.2499 27.0011C21.4049 27.0011 20.3424 26.2061 18.2174 24.6161C7.05497 16.2712 8.14997 6.37875 12.9899 3.38126Z"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M24.4999 38.2511L35.9823 34.7236C36.982 34.4169 38.0529 34.4338 39.0424 34.7718C40.0319 35.1099 40.8893 35.7518 41.4923 36.6061C42.4148 37.8811 42.0398 39.7111 40.6948 40.4861L21.9074 51.3285C21.3199 51.6684 20.6694 51.8852 19.9955 51.9655C19.3216 52.0459 18.6383 51.9882 17.9874 51.796L2 47.051"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    stats: 23,
    subtitle: "Pasien Loyal",
    image: (
      <svg
        className="stroke-[3px] stroke-white"
        width="56"
        height="50"
        viewBox="0 0 56 50"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M41.4879 33.392C42.69 32.5709 44.0934 32.0935 45.5469 32.011C47.0003 31.9286 48.4487 32.2443 49.736 32.9241C51.0233 33.604 52.1006 34.6222 52.852 35.8691C53.6034 37.1159 54.0003 38.5442 54 40C54 40.2151 53.9911 40.4293 53.9733 40.6426C50.7736 41.7604 47.3753 42.1955 43.9972 41.92C43.987 38.8976 43.1166 35.9379 41.4879 33.392ZM41.4879 33.392C40.0413 31.1256 38.047 29.2603 35.6891 27.9683C33.3312 26.6763 30.6857 25.9994 27.997 26C25.3088 25.9998 22.6639 26.677 20.3064 27.9689C17.949 29.2609 15.9551 31.126 14.5089 33.392M43.9945 41.9173L43.9972 42C43.9972 42.5991 43.9643 43.1911 43.8985 43.776C39.0596 46.5528 33.5761 48.0094 27.997 48C22.2103 48 16.7782 46.464 12.0955 43.776C12.0266 43.1588 11.9936 42.5382 11.9968 41.9173M11.9968 41.9173C8.61982 42.2022 5.22343 41.7687 2.02604 40.6453C1.90431 39.1401 2.21109 37.6312 2.91093 36.293C3.61076 34.9549 4.67505 33.8421 5.98076 33.0834C7.28646 32.3247 8.78024 31.951 10.2894 32.0057C11.7985 32.0603 13.2614 32.5409 14.5089 33.392M11.9968 41.9173C12.0063 38.8952 12.881 35.9382 14.5089 33.392M35.9971 9.99999C35.9971 12.1217 35.1543 14.1566 33.6539 15.6568C32.1536 17.1571 30.1188 18 27.997 18C25.8753 18 23.8404 17.1571 22.3401 15.6568C20.8398 14.1566 19.9969 12.1217 19.9969 9.99999C19.9969 7.87826 20.8398 5.84343 22.3401 4.34314C23.8404 2.84285 25.8753 2 27.997 2C30.1188 2 32.1536 2.84285 33.6539 4.34314C35.1543 5.84343 35.9971 7.87826 35.9971 9.99999ZM51.9973 18C51.9973 18.7879 51.8421 19.5681 51.5406 20.2961C51.239 21.024 50.7971 21.6855 50.2399 22.2426C49.6828 22.7998 49.0213 23.2417 48.2934 23.5433C47.5654 23.8448 46.7852 24 45.9972 24C45.2093 24 44.4291 23.8448 43.7011 23.5433C42.9731 23.2417 42.3117 22.7998 41.7545 22.2426C41.1974 21.6855 40.7554 21.024 40.4539 20.2961C40.1524 19.5681 39.9972 18.7879 39.9972 18C39.9972 16.4087 40.6293 14.8826 41.7545 13.7573C42.8798 12.6321 44.4059 12 45.9972 12C47.5886 12 49.1147 12.6321 50.2399 13.7573C51.3652 14.8826 51.9973 16.4087 51.9973 18ZM15.9969 18C15.9969 18.7879 15.8417 19.5681 15.5401 20.2961C15.2386 21.024 14.7967 21.6855 14.2395 22.2426C13.6823 22.7998 13.0209 23.2417 12.2929 23.5433C11.565 23.8448 10.7847 24 9.9968 24C9.20886 24 8.42864 23.8448 7.70067 23.5433C6.97271 23.2417 6.31127 22.7998 5.75411 22.2426C5.19695 21.6855 4.75499 21.024 4.45346 20.2961C4.15193 19.5681 3.99673 18.7879 3.99673 18C3.99673 16.4087 4.62888 14.8826 5.75411 13.7573C6.87934 12.6321 8.40548 12 9.9968 12C11.5881 12 13.1143 12.6321 14.2395 13.7573C15.3647 14.8826 15.9969 16.4087 15.9969 18Z"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    stats: 874  ,
    subtitle: "Kasus per Bulan",
    image: (
      <svg
        className="stroke-[3px] stroke-white"
        width="50"
        height="50"
        viewBox="0 0 50 50"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M17.3328 9.66644C18.6106 10.9442 21.1738 10.7193 24.9995 7.56065M24.9995 7.56065C24.4339 7.09724 23.847 6.56398 23.2387 5.96086C17.3456 0.12394 9.32113 1.26628 5.56189 5.96086C2.96544 9.20644 -3.67903 17.282 12.5846 46.0578C13.2593 47.2487 14.6009 48 16.0448 48C18.3499 48 20.1516 46.16 20.2257 44.0133C20.3867 39.4593 21.2684 34.2076 24.9995 34.2076C28.7307 34.2076 29.6149 39.4593 29.7733 44.0133C29.8474 46.16 31.6491 48 33.9542 48C35.3981 48 36.7398 47.2487 37.4145 46.0578C53.6806 17.2795 47.0336 9.20644 44.4372 5.96086C40.6779 1.26628 32.6534 0.12394 26.7603 5.96086C26.1521 6.56228 25.5652 7.09554 24.9995 7.56065Z"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
];
